import React, { useEffect, useState } from "react";
import {
  MDBCol,
  MDBContainer,
  MDBRow,
  MDBTable,
  MDBTableBody,
  MDBTabsPane,
} from "mdb-react-ui-kit";
import { format, parseISO } from "date-fns";
import axios from "axios";
import DrafterInfo from "../../LawsBillDetails/Info/DrafterInfo/DrafterInfo";
import RequestorInfo from "../../LawsBillDetails/Info/RequestorInfo/RequestorInfo";
import ByRequestOfInfo from "../../LawsBillDetails/Info/ByRequestOfInfo/ByRequestOfInfo";

const convertISOToReadableTimeFormat = (date) =>
  format(parseISO(date), "MM/dd/yy");

const convertTimestamp = (timestamp) => {
  try {
    if (!timestamp) return "";
    const datePart = timestamp.split(" ")[0];
    const [year, month, day] = datePart.split("-");
    const formattedMonth = month.padStart(2, "0");
    const formattedDay = day.padStart(2, "0");
    return `${formattedMonth}/${formattedDay}/${year}`;
  } catch (error) {
    return timestamp;
  }
};

const getMatchingNumber = (status, billType) => {
  const house = ["020", "040", "060", "090", "110", "180"]; // HOUSE FLOW
  const senate = ["020", "090", "110", "040", "060", "180"]; // SENATE FLOW
  let steps = 0;
  let dates = [];

  status.forEach((statusItem) => {
    if (statusItem.actionType.progressCategory) {
      const code = statusItem.actionType.progressCategory.code.toString();
      if (billType === "H" && house.includes(code)) {
        const index = house.indexOf(code) + 1;
        steps = Math.max(steps, index);
        dates.push(convertISOToReadableTimeFormat(statusItem.date));
      } else if (billType === "S" && senate.includes(code)) {
        const index = senate.indexOf(code) + 1;
        steps = Math.max(steps, index);
        dates.push(convertISOToReadableTimeFormat(statusItem.date));
      }
    }
  });

  return { steps, dates };
};

const Summary = ({
  active,
  bill,
  onClickOpenTab,
  ordinal,
  onLoad,
  onError,
}) => {
  const params = new URLSearchParams();
  params.append("legislatureOrdinal", `${ordinal}`);
  params.append("sessionOrdinal", `${bill.id.sessionId}`);
  params.append("billType", `${bill.billType}`);
  params.append("billNumber", `${bill.billNumber}`);

  let billDraft = null;
  const [billDraftRequester, setBillDraftRequester] = useState(null);
  const [billDrafter, setBillDrafter] = useState(null);

  if (bill.sessionId < 1000) {
    billDraft = bill.draft;
    const primarySponsor = bill.primarySponsorBillRoles?.[0];
    const { steps, dates } = false;
    const billAmendmentsAvailableinBillActions = "";
    const [step, setStep] = useState(steps);
    const [legislatorProfilePic, setLegislatorProfilePic] = useState(false);
    const [legislatorProfileLink, setLegislatorProfileLink] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [billSection, setBillSection] = useState([]);
    const [chapterPdfUrl, setChapterPdfUrl] = useState();
    const sortDateWithAscendingStatus = bill.draft.billStatuses.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  } else {
    const primarySponsor = bill.primarySponsorBillRoles?.[0];
    const BillTypeChar = bill.billType
      ? bill.billType.charAt(0).toUpperCase()
      : "H";
    const { steps, dates } = getMatchingNumber(bill.billActions, BillTypeChar);
    const billAmendmentsAvailableinBillActions = bill.billActions.find(
      (item) => typeof item.amendmentFileName === "string"
    );
    const [billType, setBillType] = useState(BillTypeChar);
    const [step, setStep] = useState(steps);
    const [legislatorProfilePic, setLegislatorProfilePic] = useState(false);
    const [legislatorProfileLink, setLegislatorProfileLink] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [billSection, setBillSection] = useState([]);
    const [chapterPdfUrl, setChapterPdfUrl] = useState();
    const sortDateWithAscendingStatus = bill.billActions.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }

  useEffect(() => {
    async function fetchBillSection() {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAWS_ARCHIVE_BACKEND_HOST}/v1/billSections/${bill.id.sessionId}/${bill.id.billDraftNumber}`
        );
        setBillSection(response.data);
      } catch (error) {
        console.error("Error fetching bill section: ", error);
      }
    }

    async function fetchChapters() {
      if (
        params.legislatureOrdinal &&
        params.sessionOrdinal &&
        params.billType &&
        params.billNumber
      ) {
        try {
          const response = await axios.get(
            `${
              process.env.REACT_APP_LAWS_BACKEND_HOST
            }/docs/v1/documents/getBillChapters?${params.toString()}`
          );
          return response.data;
        } catch (error) {
          console.error("Error fetching chapters: ", error);
        }
      }
    }

    function padNumber(number, prefix, suffix, length) {
      return `${prefix}${number.toString().padStart(length, "0")}.${suffix}`;
    }

    async function fetchSingleChapter() {
      try {
        let archiveChapterDoc = padNumber(
          bill.sessionLawChapterNumber
            ? bill.sessionLawChapterNumber.toString()
            : "",
          "CH",
          "pdf",
          4
        );
        const response = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/findByName?documentName=${archiveChapterDoc}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching chapters: ", error);
      }
    }

    async function fetchChapterPdfUrl(chapterId) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/shortPdfUrl?documentId=${chapterId}`
        );
        setChapterPdfUrl(response.data);
      } catch (error) {
        console.error("Error fetching chapter PDF URL: ", error);
      }
    }

    async function fetchNonStandingCommitteeData(
      nonStandingCommitteeId,
      legislatureId
    ) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/nonStandingCommittees/search?limit=10&offset=0`,
          {
            nonStandingCommitteeIds: [nonStandingCommitteeId],
            legislatureIds: [legislatureId],
          },
          {
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching committee data: ", error);
        throw error;
      }
    }

    async function fetchStandingCommitteeData(
      StandingCommitteeId,
      legislatureId
    ) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/StandingCommittees/search?limit=10&offset=0`,
          {
            standingCommitteeIds: [StandingCommitteeId],
            legislatureIds: [legislatureId],
          },
          {
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching committee data: ", error);
        throw error;
      }
    }

    async function fetchLegislatorData(requestorId) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/${requestorId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching bill draft data: ", error);
      }
    }

    async function fetchDrafterById(drafterId) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/staffMembers/${drafterId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching bill draft data: ", error);
      }
    }

    async function fetchBillDraftAll(requesterId, requestorType, drafterId) {
      let data;
      let drafterData;
      if (requestorType === "NON_STANDING_COMMITTEE") {
        data = await fetchNonStandingCommitteeData(requesterId, 2);
      } else if (requestorType === "STANDING_COMMITTEE") {
        data = await fetchStandingCommitteeData(requesterId, 2);
      } else if (requestorType === "LEGISLATOR") {
        data = await fetchLegislatorData(requesterId);
      }
      drafterData = await fetchDrafterById(drafterId);
      let requesterData = await fetchDrafterById(requesterId);
      setBillDraftRequester(requesterData);
      setBillDrafter(drafterData);
    }

    async function fetchAll() {
      try {
        await fetchBillSection();
        const chapterPdfDoc = await fetchSingleChapter();
        if (chapterPdfDoc) {
          await fetchChapterPdfUrl(chapterPdfDoc[0].id);
        }
      } catch (error) {
        console.error("Error fetching all data: ", error);
      }
    }

    if (bill.sessionId < 1000) {
      fetchBillDraftAll(
        billDraft.requesterId,
        billDraft.requesterType,
        bill.drafterStaffMemberId
      );
    } else {
      fetchAll();
    }
    onLoad();
  }, []);

  return (
    <MDBTabsPane show={active}>
      <MDBContainer fluid>
        <MDBRow className="g-2">
          <MDBCol md="9" className="ps-0">
            {billDraft ? (
              <MDBTable small>
                <MDBTableBody>
                  <tr>
                    <th scope="row" width="225">
                      Draft #
                    </th>
                    <td>
                      {billDraft.draftNumber == null
                        ? "No Draft number"
                        : billDraft.draftNumber}
                    </td>
                  </tr>
                  {billDraft && (
                    <tr>
                      <th scope="row">Drafter</th>
                      <td>
                        <DrafterInfo drafterId={bill.drafterStaffMemberId} />
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th scope="row">Requester</th>
                    <td>
                      <RequestorInfo
                        requestorId={bill.draft.requesterId}
                        requestorType={bill.draft.requesterType}
                        legislatureId={ordinal}
                      />
                    </td>
                  </tr>
                  {bill.draft.byRequestOfs != null &&
                    bill.draft.byRequestOfs.length > 0 && (
                      <tr>
                        <th scope="row">By Request of</th>
                        <td>
                          <ByRequestOfInfo
                            requestors={bill.draft.byRequestOfs}
                          />
                        </td>
                      </tr>
                    )}
                  <tr>
                    <th scope="row">Preintroduction Required</th>
                    <td>{billDraft.preIntroRequired ? "Yes" : "No"}</td>
                  </tr>
                  {billDraft.fiscalNote && (
                    <tr>
                      <th scope="row">Fiscal Analyst</th>
                      <td>Yes</td>
                    </tr>
                  )}
                  {billDraft.legalNote && (
                    <tr>
                      <th scope="row">Legal Note</th>
                      <td>Yes</td>
                    </tr>
                  )}
                  {/*<tr>
                                        <th scope="row">Category</th>
                                        <td>
                                            {billDraft.deadlineCategory?.name ? billDraft.deadlineCategory.name : ""}
                                        </td>
                                    </tr>*/}
                  <tr>
                    <th scope="row">Subject(s)</th>
                    <td>
                      {billDraft &&
                      billDraft.subjects &&
                      billDraft.subjects.length > 0 ? (
                        billDraft.subjects.map((subject, index) => (
                          <span key={index}>
                            {subject.subjectCode.description}
                            {index < billDraft.subjects.length - 1 ? ", " : ""}
                          </span>
                        ))
                      ) : (
                        <span></span>
                      )}
                    </td>
                  </tr>
                </MDBTableBody>
              </MDBTable>
            ) : (
              <MDBTable small>
                <MDBTableBody>
                  <tr>
                    <th scope="row" width="225">
                      Draft #
                    </th>
                    <td>
                      {bill.id.billDraftNumber == null
                        ? "No Draft number"
                        : bill.id.billDraftNumber}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Drafter</th>
                    <td>
                      {bill.drafters.length > 0
                        ? bill.drafters.map((drafter, index) => (
                            <span key={index}>
                              {drafter.lawEntity?.lastName},{" "}
                              {drafter.lawEntity?.firstName}
                            </span>
                          ))
                        : ""}
                    </td>
                  </tr>
                  {bill.requesters.length > 0 && (
                    <tr>
                      <th scope="row">Requester</th>
                      <td>
                        {bill.requesters.map((requester, index) => (
                          <span key={index}>
                            {requester.lawEntity &&
                            (requester.lawEntity.lastName ||
                              requester.lawEntity.firstName)
                              ? `${requester.lawEntity.lastName || ""}${
                                  requester.lawEntity.lastName &&
                                  requester.lawEntity.firstName
                                    ? ", "
                                    : ""
                                }${requester.lawEntity.firstName || ""}`
                              : ""}
                          </span>
                        ))}
                      </td>
                    </tr>
                  )}
                  {bill.requestOf.length > 0 && (
                    <tr>
                      <th scope="row">By Request Of</th>
                      <td>
                        {bill.requestOf.map((requestOf, index) => (
                          <span key={index}>
                            {" "}
                            {requestOf.lawEntity &&
                            (requestOf.lawEntity.lastName ||
                              requestOf.lawEntity.firstName)
                              ? `${requestOf.lawEntity.lastName || ""}${
                                  requestOf.lawEntity.lastName &&
                                  requestOf.lawEntity.firstName
                                    ? ", "
                                    : ""
                                }${requestOf.lawEntity.firstName || ""}`
                              : ""}{" "}
                          </span>
                        ))}
                      </td>
                    </tr>
                  )}
                  {bill.fiscalNote === "Y" && (
                    <tr>
                      <th scope="row">Fiscal Analyst</th>
                      <td>&nbsp;</td>
                    </tr>
                  )}
                  <tr>
                    <th scope="row">Preintroduction Required</th>
                    <td>
                      {bill.preintroductionRequired === "Y" ? "Yes" : "No"}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Category</th>
                    <td>
                      {bill.deadlineCategory?.name
                        ? bill.deadlineCategory.name
                        : ""}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Subject(s)</th>
                    <td>
                      {bill.subjects.map((subject, index) => (
                        <span key={index}>
                          {subject.subject.description + ", "}
                        </span>
                      ))}
                    </td>
                  </tr>
                </MDBTableBody>
              </MDBTable>
            )}
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </MDBTabsPane>
  );
};

export default Summary;
